import { css, html, LitElement, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { isValidDate } from '../modules/object';
import { ICONS } from '../modules/resources';
import { parseFunction } from '../modules/scripter';
import { KryptonElement, FormFactor } from './krypton-element';
import { KryptonEventDetail } from './krypton-event-detail';

type COLUMN_TYPE = "general" | "numeric" | "date";

abstract class TableHelper extends KryptonElement {

    connectedCallback() {
        this.formFactor = this.formFactor;
        super.connectedCallback();
    }

    _selectable: boolean = false;

    get selectable() {
        return this._selectable;
    }

    @property({ type: Boolean, reflect: true })
    set selectable(selectable) {
        const oldValue = this._selectable;
        this._selectable = selectable;
        this.toggleSelectable(selectable);
        this.requestUpdate("selectable", oldValue);
    }

    protected toggleSelectable(selectable: boolean = this.selectable) {
        const cell = this.shadowRoot?.getElementById("select-cell") as TableCell
            , input = this.shadowRoot?.getElementById("select-input") as HTMLInputElement;
        if (!cell || !input)
            return;
        selectable ? input.removeAttribute("disabled") : input.setAttribute("disabled", "true");
    }

    _disabledSelectable: boolean = false;

    get disabledSelectable() {
        return this._disabledSelectable;
    }

    @property({ type: Boolean, reflect: true, attribute: "disabled-selectable" })
    set disabledSelectable(disabledSelectable) {
        const oldValue = this._disabledSelectable;
        this._disabledSelectable = disabledSelectable;
        this.toggleDisabledSelectable(disabledSelectable);
        this.requestUpdate("disabled-selectable", oldValue);
    }

    protected toggleDisabledSelectable(disabledSelectable: boolean = this.disabledSelectable) {
        const input = this.shadowRoot?.getElementById("select-input") as HTMLInputElement;
        disabledSelectable ? input.setAttribute("disabled", "true") : input.removeAttribute("disabled");
    }

    get selected() {
        const input = this.shadowRoot?.getElementById("select-input") as HTMLInputElement;
        return input.checked;
    }

    @property({ type: Boolean })
    set selected(selected) {
        const input = this.shadowRoot?.getElementById("select-input") as HTMLInputElement
            , oldValue = input.checked;
        this.toggleSelected(selected);
        this.requestUpdate("selected", oldValue);
    }

    protected toggleSelected(selected: boolean = this.selected): boolean {
        const input = this.shadowRoot?.getElementById("select-input") as HTMLInputElement;
        if (!input)
            return false;
        if (input.checked = selected) {
            this.classList.add("selected");
            if (typeof this.onSelect == "function") this.onSelect();
            this.fire(KryptonEventDetail.TABLE_EVENT, new KryptonEventDetail(KryptonEventDetail.SELECTED, KryptonEventDetail.TABLE_EVENT, { selected: selected }));
            return true;
        } else {
            this.classList.remove("selected");
            if (this.classList.length == 0) this.removeAttribute("class");
            this.fire(KryptonEventDetail.TABLE_EVENT, new KryptonEventDetail(KryptonEventDetail.SELECTED, KryptonEventDetail.TABLE_EVENT, { selected: selected }));
            return false;
        }
    }

    _onSelect: Function;

    get onSelect() {
        return this._onSelect;
    }

    @property({ type: Function, attribute: "on-select", reflect: true, converter: {} })
    set onSelect(onSelect: Function | string) {
        const oldValue = this._onSelect;
        if (typeof onSelect == "function") {
            this._onSelect = onSelect;
        } else {
            this._onSelect = parseFunction(onSelect);
        }
        this.requestUpdate("on-select", oldValue);
    }

    get formFactor() {
        return KryptonElement.formFactor();
    }

    set formFactor(formFactor: FormFactor) {
        switch (formFactor) {
            case "desktop":
                this.classList.remove("m");
                this.classList.remove("t");
                break;
            case "tablet":
                this.classList.remove("m");
                this.classList.add("t");
                break;
            case "mobile":
                this.classList.remove("t");
                this.classList.add("m");
                break;
        }
        if (this.classList.length == 0) this.removeAttribute("class");
    }

    onMediaMobile(e: MediaQueryListEvent) {
        if (e.matches) this.formFactor = "mobile";
    }

    onMediaTablet(e: MediaQueryListEvent) {
        if (e.matches) this.formFactor = "tablet";
    }

    onMediaDesktop(e: MediaQueryListEvent) {
        if (e.matches) this.formFactor = "desktop";
    }

}

export class TableElement extends TableHelper {

    _headers: string[] = [];

    get headers() {
        return this._headers;
    }

    @property({ type: Array, attribute: true })
    set headers(headers) {
        const oldValue = this._headers;
        this._headers = headers;
        if (headers.length) {
            const slot = this.shadowRoot?.querySelector("slot[name='head']") as HTMLSlotElement
            if (slot) slot.assignedNodes().forEach(n => n.parentNode?.removeChild(n));
            headers.forEach(h => {
                const column = new TableCell();
                column.innerText = h.split("|")[0];
                column.setAttribute("slot", "head");
                this.appendChild(column);
            });
        }
        this.requestUpdate("headers", oldValue);
    }

    get types(): COLUMN_TYPE[] {
        const types: COLUMN_TYPE[] = [];
        this.headers.forEach(h => types.push(h.split("|")[1] as COLUMN_TYPE || "general"));
        return types;
    }

    get rows(): TableRow[] {
        const shadowRows = [].slice.call(this.shadowRoot?.querySelectorAll("t-row")) as TableRow[];
        const lightRows = [].slice.call(this.querySelectorAll("t-row")) as TableRow[];
        return shadowRows.concat(lightRows);
    }

    get selectedRows(): TableRow[] {
        return this.rows.filter(r => r.selected);
    }

    get headerElements() {
        const shadowRows = [].slice.call(this.shadowRoot?.querySelectorAll("t-cell")) as TableCell[];
        const lightRows = [].slice.call(this.querySelectorAll("t-cell[slot='head']")) as TableCell[];
        return shadowRows.concat(lightRows);
    }

    get selectable() {
        return super.selectable;
    }

    set selectable(selectable) {
        super.selectable = selectable;
        const rows = this.rows;
        if (rows && rows.length) rows.forEach(r => r.selectable = selectable);
    }

    set formFactor(formFactor: FormFactor) {
        super.formFactor = formFactor;
        this.rows.forEach(r => r.formFactor = formFactor);
        this.headerElements.forEach(c => c.formFactor = formFactor);
    }

    get value() {
        const value: Object[] = [];
        const selectedRows: TableRow[] = this.selectedRows;
        selectedRows.forEach(r => value.push(r.value));
        return value;
    }

    onSlotChange(e: Event) {
        this.rows.forEach(r => {
            r.selectable = this.selectable;
            let columns = r.columns;
            for (let i = 0; i < this.headers.length; i++) {
                let h = this.headers[i]
                    , hParts = h.split("|")
                    , innerText: string = hParts[0]
                    , type: COLUMN_TYPE = (hParts[1] || "general") as COLUMN_TYPE;
                columns[i].setAttribute("header", innerText);
                if (type) columns[i].type = type;
            }
        });
    }

    onClickSelect(e: Event) {
        const selected = this.toggleSelected();
        this.rows.forEach(r => r.selected = selected);
    }

    connectedCallback() {
        KryptonElement.mediaQuerySmall.addListener(this.onMediaMobile.bind(this));
        KryptonElement.mediaQueryMedium.addListener(this.onMediaTablet.bind(this));
        KryptonElement.mediaQueryLarge.addListener(this.onMediaDesktop.bind(this));
        super.connectedCallback();
    }

    disconnectedCallback() {
        KryptonElement.mediaQuerySmall.removeListener(this.onMediaMobile.bind(this));
        KryptonElement.mediaQueryMedium.removeListener(this.onMediaTablet.bind(this));
        KryptonElement.mediaQueryLarge.removeListener(this.onMediaDesktop.bind(this));
        super.disconnectedCallback();
    }

    firstUpdated(changedProperties: PropertyValues) {
        this.toggleSelectable();
        const formFactor = KryptonElement.formFactor();
        this["onMedia" + formFactor.charAt(0).toUpperCase() + formFactor.slice(1)]({ matches: true });
        super.firstUpdated(changedProperties);
    }

    static styles = css`
        :host {
            display: table;
            width: 100%;
        }

        t-head {
            background-color: var(--krypton-color-grey);
            font-weight: 700;
            text-align: center;
            line-height: 1.25em;
        }

        :host(:not([selectable])) #select-cell {
            display: none;
        }

        #select-cell {
            text-align: left;
        }

        #select-cell::before {
            display: none !important;
        }

        :host(.m[selectable]) #select-input,
        :host(.t[selectable]) #select-input {
            display: table-cell;
            margin-top: 0.5em;
            margin-left: 0.5em;
        }

        :host(.t) t-head,
        :host(.m) t-head {
            display: table;
            width: 100%;
            border-bottom: 1px black solid;
            border-collapse: collapse;
        }

        :host(.t) t-head ::slotted(t-cell),
        :host(.m) t-head ::slotted(t-cell) {
            display: none;
        }

        ::slotted(t-cell) {
            white-space: nowrap;
        }

        ::slotted(t-row) {
            background-color: var(--krypton-color-white);
            transition: opacity 250ms linear;
        }

        ::slotted(t-row:nth-of-type(2n)) {
            background-color: var(--krypton-color-grey);
        }

        :host(.row-expand) ::slotted(t-row) {
            opacity: 0.55;
        }

        :host(.row-expand) ::slotted(t-row.expand) {
            opacity: 1;
        }
        `;

    render() {
        return html`
            <t-head>
                <t-cell id="select-cell">
                    <input id="select-input" type="checkbox" disabled @click="${this.onClickSelect}">
                </t-cell>
                <slot name="head"></slot>
            </t-head>
            <t-body>
                <slot @slotchange="${this.onSlotChange}"></slot>
            </t-body>
        `;
    }

}

export class TableHeader extends LitElement {

    static styles = css`
        :host {
            display: table-header-group;
        }`;

    render() {
        return html`<slot></slot>`;
    }

}

export class TableBody extends LitElement {

    static styles = css`
        :host {
            display: table-row-group;
        }`;

    render() {
        return html`<slot></slot>`;
    }

}

export class TableRow extends TableHelper {

    set formFactor(formFactor: FormFactor) {
        super.formFactor = formFactor;
        this.columns.forEach(c => c.formFactor = formFactor);
    }

    onMediaDesktop(e: MediaQueryListEvent) {
        super.onMediaDesktop(e);
        if (e.matches) {
            this.classList.remove("expand");
            if (this.classList.length == 0) this.removeAttribute("class");
            this.table?.classList.remove("row-expand");
            if (this.table?.classList.length == 0) this.table?.removeAttribute("class");
        }
    }

    onClickChevron(e: Event) {
        this.expand = !this.expand;
    }

    _expand: boolean = false;

    get expand() {
        return this._expand;
    }

    @property({ type: Boolean })
    set expand(expand) {
        const oldValue = this._expand;
        this._expand = expand;
        if (expand) {
            this.classList.add("expand");
            this.table?.classList.add("row-expand");
        } else {
            this.classList.remove("expand");
            if (this.classList.length == 0) this.removeAttribute("class");
            if (this.table?.querySelectorAll("t-row.expand").length == 0) {
                this.table?.classList.remove("row-expand");
                if (this.table?.classList.length == 0) this.table?.removeAttribute("class");
            }
        }
        this.requestUpdate("expand", oldValue);
    }

    get columns() {
        const lightColumns = [].slice.call(this.querySelectorAll("t-cell")) as TableCell[];
        const shadowColumns = [].slice.call(this.shadowRoot?.querySelectorAll("t-cell")) as TableCell[];
        return lightColumns.concat(shadowColumns);
    }

    get table() {
        return this.parentElement;
    }

    get value() {
        const value: Object[] = [];
        const lightColumns = [].slice.call(this.querySelectorAll("t-cell")) as TableCell[];
        lightColumns.forEach(c => value.push({ name: c.name, type: c.type, value: c.value }));
        return value;
    }

    onClickSelect(e: Event) {
        this.toggleSelected();
    }

    connectedCallback() {
        KryptonElement.mediaQueryLarge.addListener(this.onMediaDesktop.bind(this));
        super.connectedCallback();
    }

    disconnectedCallback() {
        KryptonElement.mediaQueryLarge.removeListener(this.onMediaDesktop.bind(this));
        super.disconnectedCallback();
    }

    firstUpdated(changedProperties: PropertyValues) {
        this.toggleSelectable();
        this.toggleSelected();
        super.firstUpdated(changedProperties);
    };

    static styles = css`
        :host {
            display: table-row;
            line-height: 1.75em;
        }

        :host(.t),
        :host(.m) {
            display: table;
            width: 100%;
            border-bottom: 1px #666666 solid;
            border-collapse: collapse;
        }

        :host(.selected),
        :host(.m.expand.selected),
        :host(.t.expand.selected) {
            background-color: #D0E2EF !important;
        }

        :host(:not([selectable])) #select-cell {
            display: none;
        }

        #select-cell::before {
            display: none !important;
        }

        :host(.m[selectable]) #select-input,
        :host(.t[selectable]) #select-input {
            display: table-cell;
            margin-top: 0.5em;
            margin-left: 0.5em;
        }

        :host(.m) ::slotted(t-cell:nth-child(n+4)),
        :host(.t) ::slotted(t-cell:nth-child(n+4))  {
            display: none;
        }

        :host(.m.expand) ::slotted(t-cell:nth-child(n+4)),
        :host(.t.expand) ::slotted(t-cell:nth-child(n+4))  {
            display: table-row;
        }

        :host(.m.expand),
        :host(.t.expand) {
            background-color: #f0f8ff !important;
            -webkit-box-shadow: 0px 0px 11px 2px rgba(0,0,0,0.65);
            -moz-box-shadow: 0px 0px 11px 2px rgba(0,0,0,0.65);
            box-shadow: 0px 0px 11px 2px rgba(0,0,0,0.65);
            margin: -2px -0.25em 1em;
            width: calc(100% + 0.5em);
            z-index: 100;
        }

        #expand-cell {
            text-align: right;
        }

        #expand-cell::before {
            content: '';
            display: table-cell;
        }

        :host(:not(.m):not(.t)) #expand-cell {
            display: none;
        }

        #expand-img {
            margin-top: 0.25em;
        }

        :host(.m.expand) #expand-img,
        :host(.t.expand) #expand-img {
            transform: rotate(180deg);
        }
        `;

    render() {
        return html`
            <t-cell id="select-cell">
                <input type="checkbox" id="select-input" disabled @click="${this.onClickSelect}">
            </t-cell>
            <slot></slot>
            <t-cell id="expand-cell">
                <img id="expand-img" src="${ICONS.chevron_circle.file}" @click="${this.onClickChevron}">
            </t-cell>
            `;
    }

}

export class TableCell extends TableHelper {

    @property({ type: String, reflect: true }) accessor type: COLUMN_TYPE;
    @property({ type: String, attribute: "header", reflect: true }) accessor header: string;

    constructor() {
        super();
        if (!this.value) this.value = this.innerText;
    }

    _name: string;

    get name() {
        return this._name || this.header;
    }

    @property({ type: String, attribute: false })
    set name(name) {
        const oldValue = this._name;
        this._name = name;
        this.requestUpdate("name", oldValue);
    }

    _value: any;

    get value() {
        try {
            return this.parseValue(this._value, this.type);
        } catch (err) {
            console.warn(this._value, ", could not be parsed as type '" + this.type + "'");
            return this._value;
        }
    }

    @property({ type: Object, reflect: true })
    set value(value) {
        const oldValue = this._value;
        this._value = value;
        this.requestUpdate("value", oldValue);
    }

    parseValue(value: string, type: COLUMN_TYPE) {
        switch (type) {
            case "date":
                let date = new Date(value);
                if (isValidDate(date)) return date;
                throw Error();
            case "numeric":
                if (/^(\d*)(\.)?(\d*)$/.test(value)) return parseFloat(value);
                throw Error();
            case "general":
            default:
                return value;
        }
    }

    static styles = css`
        :host{
            display: table-cell;
            padding: 0.2em 0.5em;
            white-space: nowrap;
            line-height: 1.75em;
            vertical-align: middle;
        }

        :host(.m),
        :host(.t) {
            display: table-row;
            text-align: left;
            border-bottom: 1px dotted #dfdfdf;
        }

        :host(.m)::before,
        :host(.t)::before {
            display: table-cell;
            content: attr(header);
            text-align: left !important;
            padding: 0.2em 0.5em;
        }

        :host([type='numeric']:not(.m):not(.t)) {
            text-align: right;
        }

        :host([type='label']:not(.m):not(.t)) {
            text-align: right;
        }

        :host([type='date']:not(.m):not(.t)) {
            text-align: center;
        }
    `;

    render() {
        return html`<slot></slot>`;
    }

}

declare global {
    interface HTMLElementTagNameMap {
        't-element': TableElement;
        't-head': TableHeader;
        't-body': TableBody;
        't-row': TableRow;
        't-cell': TableCell;
    }
}